/// <amd-module name="Wit/WorkItemWikiLinks/Components/WikiLinkForm" />
import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import { ITfsPageService } from "Tfs/Platform/Page";
import { usePageContext } from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";
import { useDebouncedSubscription, useObservable } from "VSS/Core/Observable";
import { TextField } from "VSSUI/TextField";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";

import { ArtifactsUpdatedCallback } from "Wit/WorkItemLinksRegistration/IRegisteredLinkTypeService";
import { IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { WikiPage, WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import * as Resources from "Wit/WorkItemWikiLinks/Resources";
import { WikiPagePickerDialog } from "./WikiPagePickerDialog";

interface IWikiLinkFormProps {
    onArtifactsUpdated: ArtifactsUpdatedCallback;
    getDisplayData: (page: WikiPage, wikiId: string, projectId: string) => IResolvedLinkDisplayData;
}

export function WikiLinkForm({ onArtifactsUpdated, getDisplayData }: IWikiLinkFormProps) {
    const pageContext = usePageContext();
    const wikiClient = useMemo(() => getWikiClient(pageContext), [pageContext]);

    const [pagePickerDialogOpen, setPagePickerDialogOpen] = useObservable<boolean>(false);
    const [pagePathText, setPagePathText] = useObservable<string>("");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const wikiId = useRef<string | undefined>();
    const projectId = useRef<string | undefined>();

    const selectPage = useCallback((pagePath: string) => {
        setPagePathText(pagePath);
        setPagePickerDialogOpen(false);
    }, []);

    useDebouncedSubscription(
        pagePathText,
        300,
        async newText => {
            if (!projectId.current) {
                const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
                const project = pageService.getData()?.project;
                if (!project) {
                    onArtifactsUpdated([]);
                    setErrorMessage(Resources.ErrorNoProject);
                    return;
                }
                projectId.current = project.id;
            }

            if (!newText.trim()) {
                onArtifactsUpdated([]);
                setErrorMessage(Resources.ErrorNoPath);
                return;
            }

            if (!wikiId.current) {
                const wikis = await wikiClient.getAllWikis(projectId.current!);
                const projectWiki = wikis.find(w => w.type === WikiType.ProjectWiki);
                if (!projectWiki) {
                    onArtifactsUpdated([]);
                    setErrorMessage(Resources.ErrorNoWiki);
                    return;
                }
                wikiId.current = projectWiki?.id;
            }

            let page: WikiPage;
            try {
                page = (await wikiClient.getPage(projectId.current!, wikiId.current!, newText)).page;
            } catch (e: unknown) {
                onArtifactsUpdated([]);
                setErrorMessage(format(Resources.ErrorNoMatchingPage, newText));
                return;
            }

            const artifact = getDisplayData(page, wikiId.current!, projectId.current!);
            onArtifactsUpdated([artifact]);
            setErrorMessage(undefined);
        },
        []
    );

    return (
        <>
            <FormItem className="wiki-link-form-item" label={Resources.WikiPageLabel} error={!!errorMessage} message={errorMessage}>
                <div className="flex-row flex-wrap gap-8">
                    <TextField
                        containerClassName="flex-grow"
                        placeholder={Resources.WikiPagePlaceholder}
                        value={pagePathText}
                        onChange={(e, val) => setPagePathText(val)}
                    />
                    <Button text={Resources.FindPageButtonLabel} onClick={() => setPagePickerDialogOpen(true)} />
                </div>
            </FormItem>

            <ConditionalChildren renderChildren={pagePickerDialogOpen}>
                <WikiPagePickerDialog onPageChosen={selectPage} onDismiss={() => setPagePickerDialogOpen(false)} />
            </ConditionalChildren>
        </>
    );
}
